// @ts-nocheck
import {useState, useEffect, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {getMyTickets, getTicketDetail, getTicketMessages, createTicket, addReply, closeTicket} from './core/_requests'
import {CustomerTicket, CustomerTicketMessage, STATUS_LABELS} from './core/_models'
import './support.scss'

// Professional Create Ticket Modal
function CreateTicketModal({
  show,
  onClose,
  onSuccess
}: {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}) {
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!subject.trim() || !message.trim()) {
      setError('נא למלא את כל השדות')
      return
    }

    setLoading(true)
    setError('')

    try {
      await createTicket({subject, message, attachments: files})
      setSubject('')
      setMessage('')
      setFiles([])
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'שגיאה בשליחת הפנייה')
    } finally {
      setLoading(false)
    }
  }

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files) {
      setFiles([...files, ...Array.from(e.dataTransfer.files)])
    }
  }

  if (!show) return null

  return (
    <div className="support-modal-overlay" dir="rtl">
      <div className="support-modal">
        <div className="support-modal-header">
          <div>
            <h2>פנייה חדשה</h2>
            <p>נשמח לעזור לך בכל שאלה או בעיה</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="support-modal-body">
            {error && (
              <div className="error-alert">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label>נושא הפנייה <span className="required">*</span></label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="למשל: שאלה לגבי התשלום"
              />
            </div>

            <div className="form-group">
              <label>פרטי הפנייה <span className="required">*</span></label>
              <textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="ספר לנו איך נוכל לעזור..."
              />
            </div>

            <div className="form-group">
              <label>קבצים מצורפים</label>
              <div
                className={`drop-zone ${isDragging ? 'dragging' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('modal-file-upload')?.click()}
              >
                <input
                  type="file"
                  id="modal-file-upload"
                  multiple
                  onChange={(e) => e.target.files && setFiles([...files, ...Array.from(e.target.files)])}
                />
                <i className="bi bi-cloud-arrow-up"></i>
                <span>גרור קבצים לכאן או לחץ לבחירה</span>
                <small>תמונות, מסמכים, PDF (עד 10MB)</small>
              </div>

              {files.length > 0 && (
                <div className="files-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <i className="bi bi-file-earmark"></i>
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                      <button type="button" onClick={() => removeFile(index)}>
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="support-modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              ביטול
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (
                <><span className="spinner"></span> שולח...</>
              ) : (
                <><i className="bi bi-send"></i> שלח פנייה</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Ticket List Component
function TicketList({
  tickets,
  selectedId,
  onSelect,
  onNewTicket
}: {
  tickets: CustomerTicket[]
  selectedId: string | null
  onSelect: (id: string) => void
  onNewTicket: () => void
}) {
  return (
    <div className="ticket-sidebar">
      <div className="sidebar-header">
        <h3>הפניות שלי</h3>
        <button className="new-ticket-btn" onClick={onNewTicket}>
          <i className="bi bi-plus-lg"></i>
          פנייה חדשה
        </button>
      </div>

      <div className="ticket-list">
        {tickets.length === 0 ? (
          <div className="empty-list">
            <i className="bi bi-inbox"></i>
            <h5>אין פניות</h5>
            <p>הפניות שלך יופיעו כאן</p>
          </div>
        ) : (
          tickets.map((ticket) => (
            <div
              key={ticket._id}
              className={`ticket-item ${selectedId === ticket._id ? 'active' : ''}`}
              onClick={() => onSelect(ticket._id)}
            >
              <div className="ticket-item-header">
                <span className={`status-badge status-${ticket.status}`}>
                  {STATUS_LABELS[ticket.status]}
                </span>
                {ticket.customer_unread && <span className="unread-dot"></span>}
              </div>
              <h6 className="ticket-subject">{ticket.subject}</h6>
              <div className="ticket-meta">
                <span className="ticket-number">#{ticket.ticket_number}</span>
                <span className="ticket-date">
                  {new Date(ticket.created_at).toLocaleDateString('he-IL')}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// Ticket Detail Component
function TicketDetail({
  ticketId,
  onClose,
  onRefresh
}: {
  ticketId: string
  onClose: () => void
  onRefresh: () => void
}) {
  const [ticket, setTicket] = useState<CustomerTicket | null>(null)
  const [messages, setMessages] = useState<CustomerTicketMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadTicket()
  }, [ticketId])

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!ticketId) return
    const interval = setInterval(async () => {
      try {
        const res = await getTicketMessages(ticketId)
        if (res.data.messages.length !== messages.length) {
          setMessages(res.data.messages)
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [ticketId, messages.length])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadTicket = async () => {
    setLoading(true)
    try {
      const response = await getTicketDetail(ticketId)
      setTicket(response.data.ticket)
      setMessages(response.data.messages)
    } catch (err) {
      console.error('Failed to load ticket:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSendReply = async () => {
    if (!reply.trim()) return

    setSending(true)
    try {
      await addReply(ticketId, reply, files)
      setReply('')
      setFiles([])
      loadTicket()
      onRefresh()
    } catch (err) {
      console.error('Failed to send reply:', err)
    } finally {
      setSending(false)
    }
  }

  const handleCloseTicket = async () => {
    if (!window.confirm('האם אתה בטוח שברצונך לסגור את הפנייה?')) return

    try {
      await closeTicket(ticketId)
      loadTicket()
      onRefresh()
    } catch (err) {
      console.error('Failed to close ticket:', err)
    }
  }

  if (loading) {
    return (
      <div className="ticket-detail-loading">
        <div className="spinner"></div>
        <span>טוען פנייה...</span>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="ticket-detail-error">
        <i className="bi bi-exclamation-circle"></i>
        <span>הפנייה לא נמצאה</span>
      </div>
    )
  }

  const isOpen = !['resolved', 'closed'].includes(ticket.status)

  return (
    <div className="ticket-detail">
      {/* Header */}
      <div className="ticket-detail-header">
        <button className="back-btn d-lg-none" onClick={onClose}>
          <i className="bi bi-arrow-right"></i>
        </button>
        <div className="header-info">
          <div className="header-top">
            <h4>{ticket.subject}</h4>
            <span className={`status-badge status-${ticket.status}`}>
              {STATUS_LABELS[ticket.status]}
            </span>
          </div>
          <div className="header-meta">
            <span>פנייה #{ticket.ticket_number}</span>
            <span className="separator">•</span>
            <span>נפתחה ב-{new Date(ticket.created_at).toLocaleDateString('he-IL')}</span>
          </div>
        </div>
        {isOpen && (
          <button className="close-ticket-btn" onClick={handleCloseTicket}>
            <i className="bi bi-check-circle"></i>
            סמן כנפתר
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="messages-container">
        <div className="messages-wrapper">
          {messages.map((msg) => {
            const isCustomer = msg.sender_type === 'customer'
            const isSystem = msg.sender_type === 'system'

            if (isSystem) {
              return (
                <div key={msg._id} className="system-message">
                  <span>{msg.message}</span>
                </div>
              )
            }

            return (
              <div key={msg._id} className={`message ${isCustomer ? 'customer' : 'admin'}`}>
                {!isCustomer && msg.sender_admin && (
                  <div className="message-sender">
                    <div className="avatar">
                      {msg.sender_admin.username.charAt(0).toUpperCase()}
                    </div>
                    <span>{msg.sender_admin.username}</span>
                  </div>
                )}
                <div className="message-bubble">
                  <div className="message-text">{msg.message}</div>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="message-attachments">
                      {msg.attachments.map((att, i) => (
                        <a key={i} href={att.location} target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-file-earmark"></i>
                          <span>{att.originalname}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <span className="message-time">
                  {new Date(msg.created_at).toLocaleString('he-IL')}
                </span>
              </div>
            )
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Reply Box */}
      {isOpen ? (
        <div className="reply-box">
          <div className="reply-input-wrapper">
            <textarea
              placeholder="כתוב תגובה..."
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendReply()
                }
              }}
            />
          </div>
          <div className="reply-actions">
            <div className="attachment-section">
              <input
                type="file"
                id="reply-files"
                multiple
                onChange={(e) => e.target.files && setFiles([...files, ...Array.from(e.target.files)])}
              />
              <label htmlFor="reply-files">
                <i className="bi bi-paperclip"></i>
              </label>
              {files.length > 0 && (
                <span className="files-count">{files.length} קבצים</span>
              )}
            </div>
            <button
              className="send-btn"
              onClick={handleSendReply}
              disabled={!reply.trim() || sending}
            >
              {sending ? (
                <span className="spinner"></span>
              ) : (
                <><i className="bi bi-send"></i> שלח</>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="ticket-closed-notice">
          <i className="bi bi-check-circle-fill"></i>
          <span>פנייה זו נסגרה</span>
        </div>
      )}
    </div>
  )
}

// Empty State Component
function EmptyState({ onNewTicket }: { onNewTicket: () => void }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <i className="bi bi-headset"></i>
      </div>
      <h3>מרכז התמיכה</h3>
      <p>בחר פנייה מהרשימה כדי לצפות בה, או פתח פנייה חדשה ונשמח לעזור</p>
      <button onClick={onNewTicket}>
        <i className="bi bi-plus-lg"></i>
        פתח פנייה חדשה
      </button>
    </div>
  )
}

// Main Support Page
export function SupportWrapper() {
  const [searchParams] = useSearchParams()
  const [tickets, setTickets] = useState<CustomerTicket[]>([])
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    const ticketFromUrl = searchParams.get('ticket')
    if (ticketFromUrl) {
      setSelectedTicketId(ticketFromUrl)
    }
  }, [searchParams])

  useEffect(() => {
    loadTickets()
  }, [])

  const loadTickets = async () => {
    setLoading(true)
    try {
      const response = await getMyTickets()
      setTickets(response.data.tickets)
    } catch (err) {
      console.error('Failed to load tickets:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="support-page" dir="rtl">
      <PageTitle breadcrumbs={[]}>תמיכה</PageTitle>

      <div className="support-container">
        {/* Sidebar */}
        <div className={`support-sidebar ${selectedTicketId ? 'hidden-mobile' : ''}`}>
          {loading ? (
            <div className="sidebar-loading">
              <div className="spinner"></div>
              <span>טוען פניות...</span>
            </div>
          ) : (
            <TicketList
              tickets={tickets}
              selectedId={selectedTicketId}
              onSelect={setSelectedTicketId}
              onNewTicket={() => setShowCreateModal(true)}
            />
          )}
        </div>

        {/* Main Content */}
        <div className={`support-main ${!selectedTicketId ? 'hidden-mobile' : ''}`}>
          {selectedTicketId ? (
            <TicketDetail
              ticketId={selectedTicketId}
              onClose={() => setSelectedTicketId(null)}
              onRefresh={loadTickets}
            />
          ) : (
            <EmptyState onNewTicket={() => setShowCreateModal(true)} />
          )}
        </div>
      </div>

      <CreateTicketModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadTickets}
      />
    </div>
  )
}
